<script>
import Vue from 'vue'
import BaseResource from '../resources'

export default Vue.extend({
    name: 'Autocomplete',

    props: {
        // Name of input field.  Not really necessary, but helpful if we use the form object down the road
        name: {
            required: false,
            type: String,
        },

        // Passed in data as part of a form set
        field: {
            required: false,
            type: Object,
        },

        // Passed in data as part of a form set
        resourceInstance: {
            required: false,
            type: Object,
        },

        // Properties to display after a selection is made, e.g. name.
        displayProp: {
            required: false,
            type: String,
            default: 'name',
        },

        // A list of css classes
        classes: {
            required: false,
            type: String,
            default: 'form-control',
        },

        // Force a name and do not use the autocomplete/lookup
        preSelectedValue: {
            required: false,
            type: [String, Number],
            default: '',
        },

        // Placeholder text for input
        placeholder: {
            required: false,
            type: String,
            default: '',
        },

        // To mark this field as required
        required: {
            required: false,
            type: Boolean,
            default: false,
        },

        // To mark this field as readonly/disabled
        readonly: {
            required: false,
            type: Boolean,
            default: false,
        },

        // Used to identify the model.  Useful for changed fields
        dataModel: {
            type: String,
            required: false,
        },

        // This is for a traditional array of options to be passed in
        // The list of options.  Should be an array of objects or a Rest Resource
        options: {
            required: false,
            type: Array,
            default() {
                return []
            },
        },

        // Properties of the options to search against, e.g. name, code, description
        searchProps: {
            required: false,
            type: Array,
            default() {
                return ['code', 'name', 'id', 'username']
            },
        },

        // This is to use Rest Resource classes
        // Should be a Rest Resource
        resourceClass: {
            required: false,
            type: Function,
        },

        resolveManagers: {
            required: false,
            type: Array,
            default: function() {
                return []
            },
        },

        // id to select for rest resource by doing a lookup
        forceSelectId: {
            required: false,
            type: [String, Number],
        },

        // Primary filter to be used with resourceClass, e.g. ?filter=
        primaryFilter: {
            required: false,
            type: String,
            default: undefined,
        },

        // Filter options that are not the primary filter.  Helps with limiting options, e.g. { type__in: 'POSTED', 'CLOSED' }
        filterOptions: {
            required: false,
            type: Object,
            default() {
                return {}
            },
        },

        // Number of characters to collect before querying API
        minKeywordLength: {
            required: false,
            type: Number,
            default: 1,
        },
        defaultShowResources: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            selected: false, // If the user has made a selection
            showList: false, // Show the options list
            isError: false, // If the input or selected field is incorrect, show an error
            keyword: '', // The actual input field
            highlightedPosition: 0, // Current list item to highlight
            query: '', // Search sent to Rest Resource API
            loading: false,
            resources: [], // Resources retrieved from search
        }
    },

    watch: {
        keyword(newValue, oldValue) {
            if (newValue === this.preSelectedValue) {
                return false // Forced display of input, no lookup
            }
            if (newValue !== oldValue && newValue.length > this.minKeywordLength) {
                this.lookup()
            }
        },

        // Handle changes made after mounting, e.g. resources loaded asynchronously
        preSelectedValue(newValue) {
            if (newValue) {
                this.keyword = newValue
            }
        },

        // Handle changes made after mounting, e.g. resources loaded asynchronously
        async forceSelectId(newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                await this.detailById(newValue)
            }
        },
        loading() {
            this.$emit('loading', this.loading)
        },
    },

    computed: {
        // Find items that match the list based on the input
        optionMatches() {
            const keyword = typeof this.keyword === 'string' ? this.keyword.replace(/[|()[\]\\]/g, '') : undefined
            const pattern = new RegExp('^' + keyword, 'i') // Look for items that start with pattern
            const loosePattern = new RegExp(keyword, 'i') // Look for items that have the pattern anywhere

            // Now go through options list and search the list of properties against the patterns
            if (this.keyword && this.options && this.options.length) {
                const matches = this.options
                    .filter((option) => {
                        for (const prop of this.searchProps) {
                            if (option && option.hasOwnProperty(prop) && option[prop] && option[prop].toString().match(pattern)) {
                                return option
                            }
                        }
                    })
                    .sort(this.optionComp)

                const looseMatches = this.options
                    .filter((option) => {
                        for (const prop of this.searchProps) {
                            if (option && option.hasOwnProperty(prop) && option[prop] && option[prop].toString().match(loosePattern)) {
                                return option
                            }
                        }
                    })
                    .sort(this.optionComp)

                // De-dupe the matches
                const results = matches.concat(looseMatches).filter((match, index, self) => self.findIndex((t) => t.id === match.id) === index)

                return results
            } else if (keyword && this.resources) {
                let matches = this.resources.map((resource) => {
                    resource.resolveRelated([...this.resolveManagers])
                    return {
                        name: (resource.attributes || {}).name,
                        code: (resource.attributes || {}).code,
                        username: (resource.attributes || {}).username,
                        id: resource.id || resource.uuid,
                        resource: resource,
                    }
                })
                return matches.sort(this.optionComp)
            } else if (this.resources && !this.keyword && this.defaultShowResources) {
                let matches = this.resources.map((resource) => {
                    resource.resolveRelated([...this.resolveManagers])
                    return {
                        name: (resource.attributes || {}).name,
                        code: (resource.attributes || {}).code,
                        username: (resource.attributes || {}).username,
                        id: resource.id || resource.uuid,
                        resource: resource,
                    }
                })
                return matches.sort(this.optionComp)
            } else {
                return []
            }
        },
    },

    methods: {
        // When something is typed into the input field, show the list and make sure to highlight the first match
        onInput(value) {
            if (!value) {
                return
            }
            this.selected = false
            this.isError = false
            this.showList = !!this.keyword
            // if user remove all value, we need to show all resources
            if (!value.data && !this.keyword && this.defaultShowResources) this.retrieveAllResources()
        },

        // When leaving the input field, select the highlighted value and return to the parent component
        onSelect(item) {
            if (this.keyword && this.keyword === this.preSelectedValue) {
                // Loaded value.  ignore
                return
            }
            if (!item || !item.target) {
                return
            }

            // The choice is what the user clicked/selected
            let choice = this.optionMatches[this.highlightedPosition]
            if (!choice) choice = this.optionMatches.find((option) => option.name === this.keyword)
            // Make sure it's a valid entry with a displayable property
            if (!choice) {
                // no match, return empty
                this.$emit('select', '')
                this.keyword = ''
                return false
            } else if (!this.keyword && this.required) {
                this.isError = true
                this.$emit('error', '')
            } else if (!this.keyword) {
                // Return empty if no value and tabbing through.
            } else if (choice && this.resources) {
                this.selected = true
                this.$emit(
                    'select',
                    this.resources.find((res) => res.id === choice.id)
                )
                this.keyword = choice[this.displayProp]
            } else if (choice && choice.hasOwnProperty(this.displayProp)) {
                this.selected = true
                this.$emit('select', choice)
                this.keyword = choice[this.displayProp]
            } else if (this.selected) {
                // Do nothing if already selected and tabbing through.
            } else {
                this.isError = true
                this.$emit('error', choice)
            }

            this.showList = false

            if (choice.resource instanceof BaseResource) {
                this.keyword = choice.name || choice.code || choice.username
            }
        },

        onKeyDown(event) {
            let keyCode = event.which || event.keyCode

            if (keyCode === 13 || keyCode === 9) {
                this.onSelect() // Enter/Return
            } else if (keyCode === 38) {
                this.moveUp() // Up
            } else if (keyCode === 40) {
                this.moveDown() // Down
            } else if (keyCode === 192) {
                // Escape
                this.showList = false
                this.params.api.stopEditing()
            } else {
                // No action for now
            }

            event.stopPropagation()
        },

        // Move the highlighed item index up
        moveUp() {
            // Only scroll down if not past the last item on the list
            if (this.showList && this.optionMatches.length && this.highlightedPosition > 0) {
                this.highlightedPosition--

                // Else scroll to the bottom
            } else if (this.showList && this.optionMatches.length && this.highlightedPosition <= 0) {
                this.highlightedPosition = this.optionMatches.length - 1
            } else {
                return false
            }

            const element = 'list-items'
            this.$refs[element][this.highlightedPosition].focus()
            return true
        },

        // Move the highlighed item index down
        moveDown() {
            // Only scroll down if not past the last item on the list
            if (this.showList && this.optionMatches.length && this.highlightedPosition < this.optionMatches.length - 1) {
                this.highlightedPosition++

                // Else scroll to the top
            } else if (this.showList && this.optionMatches.length) {
                this.highlightedPosition = 0
            } else {
                return false
            }

            const element = 'list-items'
            this.$refs[element][this.highlightedPosition].focus()
            return true
        },

        // Compare options for sorting
        optionComp(a, b) {
            let aFixed
            let bFixed

            if (a.name && b.name) {
                aFixed = a.name.toLowerCase()
                bFixed = b.name.toLowerCase()
            } else if (a.code && b.code) {
                aFixed = a.code.toLowerCase()
                bFixed = b.code.toLowerCase()
            } else {
                return a - b
            }

            if (aFixed > bFixed) {
                return 1
            } else if (aFixed < bFixed) {
                return -1
            } else {
                return 0
            }
        },

        // Rest resource lookup
        async lookup() {
            let ResourceClass = this.field && this.field.resourceClass ? this.field.resourceClass : this.resourceClass
            let filterOptions = (this.field && this.field.filterOptions && typeof this.field.filterOptions === 'object' ? this.field.filterOptions : this.filterOptions) || {}
            this.loading = true

            if (this.keyword && this.primaryFilter) {
                let query = Object.assign({}, filterOptions)
                query[this.primaryFilter] = this.keyword
                let result = await ResourceClass.filter(query)
                this.resources = result.resources
            } else if (this.keyword) {
                let query = Object.assign({}, filterOptions)
                query.search = this.keyword
                let result = await ResourceClass.filter(query)
                this.resources = result.resources
            } else {
                let result = await ResourceClass.list()
                this.resources = result.resources
            }
            if (this.resources && this.resources.length && this.resolveManagers) {
                this.resources.forEach(async (resource) => {
                    await resource.resolveRelated({ managers: this.resolveManagers })
                })
            }
            this.loading = false
        },

        // Rest resource get detail
        async detailById(id) {
            let ResourceClass = this.field && this.field.resourceClass ? this.field.resourceClass : this.resourceClass

            if (id) {
                let result = await ResourceClass.detail(id)
                if (result) this.keyword = result.attributes[this.displayProp]
                this.$emit('select', result)
                return result
            } else {
                return null
            }
        },

        // Rest resource get detail
        async detailByCode(code) {
            let ResourceClass = this.field && this.field.resourceClass ? this.field.resourceClass : this.resourceClass

            if (code) {
                code = code
                let result = await ResourceClass.filter({ query: { code__eq: code } })
                if (result) this.keyword = result[0].attributes[this.displayProp]
                this.$emit('select', result[0])
                return result[0]
            } else {
                return null
            }
        },
        async retrieveAllResources() {
            if (this.resourceClass && !this.keyword && this.defaultShowResources) {
                let ResourceClass = this.resourceClass
                let results = await ResourceClass.all()
                for (let result of results) {
                    await result.resolveRelated()
                }
                this.resources = results
                this.showList = true
            }
        },
    },

    async mounted() {
        if (this.preSelectedValue) {
            this.keyword = this.preSelectedValue
        } else if (this.forceSelectId) {
            this.keyword = this.forceSelectId
            this.detailById(this.forceSelectId)
        } else if (this.resourceInstance && this.field.attribute) {
            const attribute = this.resourceInstance.attributes[this.field.attribute] || null
            if (attribute) {
                const resource = await this.field.resourceClass.detail(attribute)
                this.keyword = resource.attributes[this.displayProp] || ''
            }
        } else if (this.resourceInstance) {
            this.keyword = this.resourceInstance.attributes[this.displayProp] || ''
            if (this.resourceInstance.id) this.detailById(this.resourceInstance.id)
        }
    },
})
</script>

<template>
    <div class="autocomplete-input">
        <input
            :class="[{ 'form-error': isError }, classes]"
            :name="name + Math.floor(Math.random() * 1000000 + 1)"
            :placeholder="placeholder"
            :required="required"
            :readonly="readonly"
            :autocomplete="'force_off_' + Math.floor(Math.random() * 1000000 + 1)"
            v-model="keyword"
            :data-model="dataModel"
            @input="onInput"
            @keyup.esc="showList = false"
            @keydown.down="moveDown"
            @keydown.up="moveUp"
            @keydown.enter="onSelect"
            @keydown.tab="onSelect"
            @click="retrieveAllResources"
            @blur="
                onSelect($event)
                showList = false
            "
        />
        <ul v-show="showList" class="options-list" ref="list">
            <li
                v-for="(option, index) in optionMatches"
                :key="index"
                :class="{ highlighted: index === highlightedPosition }"
                ref="list-items"
                @mouseleave="highlightedPosition = -1"
                @mouseenter="highlightedPosition = index"
                @mousedown="onSelect"
            >
                <slot style="vertical-align: middle;" name="item" :code="option.code" :itemname="option.name" :item="option">{{ option.name }}</slot>
            </li>
        </ul>
    </div>
</template>

<style scoped>
.form-inline .form-control {
    width: 100%;
}
.options-list {
    /* list-style-type: none; */
    border: 1px solid #dbdbdb;
    width: 20%;
    min-width: 250px;
    max-width: 500px;
    min-height: 20px;
    max-height: 500px;
    overflow: scroll;
    margin-top: 5px;
    padding-inline-start: 0px;
    line-height: 2em;
    vertical-align: middle;
    background: white;
    z-index: 1060;
}
.options-list li {
    width: 100%;
    flex-wrap: wrap;
    margin: 0px;
    padding-left: 10px;
    border-bottom: 1px solid #eee;
    color: rgb(24, 37, 79);
    cursor: pointer;
    vertical-align: middle;
}
.options-list li.highlighted {
    background: #feddbb;
}
.form-error,
.form-error2 {
    background: yellow !important;
    opacity: 0.7;
    font-weight: bold;
    color: #cc0000 !important;
}
</style>
